import { useRef, useState } from 'react';
import { PageShell } from '../components/PageShell';
import { GlassPanel } from '../components/GlassPanel';
import { MaterialIcon } from '../components/MaterialIcon';
import { useGame } from '../state/gameContext';

export default function JoinRoomPage() {
  const { state, joinRoom, clearJoinError } = useGame();
  const [codeChars, setCodeChars] = useState(() => {
    const code = new URLSearchParams(window.location.search).get('code')?.toUpperCase() ?? '';
    return /^[A-Z0-9]{4}$/.test(code) ? code.split('') : ['', '', '', ''];
  });
  const [nickname, setNickname] = useState('');
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const handleCharChange = (index: number, value: string) => {
    const char = value.slice(-1).toUpperCase();
    const next = [...codeChars];
    next[index] = char;
    setCodeChars(next);
    clearJoinError();
    if (char && index < 3) inputsRef.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, key: string) => {
    if (key === 'Backspace' && !codeChars[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
  };

  const handleJoin = () => {
    joinRoom(codeChars.join(''), nickname);
  };

  return (
    <PageShell
      className="min-h-screen flex flex-col"
      mainClassName="relative z-10 flex-1 flex items-center justify-center p-margin-mobile pb-24"
    >
      <div className="w-full max-w-md flex flex-col gap-stack-lg">
        <div className="text-center space-y-2">
          <h1 className="font-headline-md text-headline-md text-primary drop-shadow-[0_0_12px_rgba(221,183,255,0.4)]">
            Join a Room
          </h1>
          <p className="text-on-surface-variant font-body-lg">Enter the secret code to enter the party.</p>
        </div>

        <GlassPanel className="p-stack-lg rounded-xl flex flex-col gap-stack-lg shadow-xl">
          <div className="space-y-stack-sm">
            <label className="font-label-bold text-label-bold text-on-surface-variant uppercase tracking-widest px-1">
              Room Code
            </label>
            <div className="flex justify-between gap-2">
              {codeChars.map((char, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    inputsRef.current[i] = el;
                  }}
                  value={char}
                  maxLength={1}
                  placeholder="-"
                  aria-label={`Room code character ${i + 1}`}
                  data-testid={`code-char-${i}`}
                  className="w-12 h-16 md:w-16 md:h-20 bg-black/30 border border-white/10 rounded-lg text-center font-display-lg text-display-lg text-primary transition-all uppercase focus:outline-none focus:border-primary"
                  onChange={(e) => handleCharChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e.key)}
                />
              ))}
            </div>
          </div>

          <div className="space-y-stack-sm">
            <label htmlFor="alias" className="font-label-bold text-label-bold text-on-surface-variant uppercase tracking-widest px-1">
              Your Alias
            </label>
            <div className="relative group">
              <input
                id="alias"
                type="text"
                placeholder="Enter Nickname"
                value={nickname}
                onChange={(e) => {
                  setNickname(e.target.value);
                  clearJoinError();
                }}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-4 font-body-lg text-on-surface focus:outline-none focus:border-primary transition-all neon-glow-primary:focus-within"
              />
              <MaterialIcon
                name="person"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-on-surface-variant group-focus-within:text-primary transition-colors"
              />
            </div>
          </div>

          <button
            type="button"
            onClick={handleJoin}
            data-testid="join-room"
            className="w-full bg-primary hover:bg-primary-container text-on-primary font-label-bold text-lg py-4 rounded-lg shadow-lg active:scale-[0.98] transition-all flex items-center justify-center gap-2"
          >
            Join Game
            <MaterialIcon name="login" />
          </button>

          {state.joinError && (
            <div className="bg-error-container/20 border border-error/30 p-4 rounded-lg flex items-start gap-3 animate-error" data-testid="join-error">
              <MaterialIcon name="warning" className="text-error mt-0.5" />
              <div>
                <p className="text-error font-label-bold text-label-bold">{state.joinError}</p>
                <p className="text-on-error-container text-label-sm">Check your code and nickname, then try again.</p>
              </div>
            </div>
          )}
        </GlassPanel>
      </div>
    </PageShell>
  );
}
